import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import logo from "./assets/comfort-logo.png";

const friendlyAuthMessage = (err) => {
  const code = String(err?.code || "");
  if (
    code.includes("invalid-credential") ||
    code.includes("wrong-password") ||
    code.includes("user-not-found")
  ) {
    return "The email or password is incorrect.";
  }
  if (code.includes("too-many-requests")) {
    return "Too many attempts. Please wait and try again.";
  }
  if (code.includes("network-request-failed")) {
    return "Network error. Please check your connection.";
  }
  return err?.message || "Login failed. Please try again.";
};

const goToSupplierHome = () => {
  // Important: React Admin's /login hash can survive a Firebase page reload.
  // Sending the authenticated user to #/ prevents a successful login from
  // immediately rendering the login route again.
  window.location.replace(`${window.location.origin}${window.location.pathname}#/`);
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState({ type: "", text: "" });

  const handleLogin = async (event) => {
    event.preventDefault();

    // Read actual form values too, because Chrome/password managers can visually
    // autofill controlled fields before React receives an onChange event.
    const form = event.currentTarget;
    const cleanEmail = (form.elements.email?.value || email || "").trim();
    const cleanPassword = form.elements.password?.value || password || "";

    if (!cleanEmail || !cleanPassword) {
      setMessage({ type: "error", text: "Please enter your email and password." });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      const credential = await firebase
        .auth()
        .signInWithEmailAndPassword(cleanEmail, cleanPassword);

      // If a role claim exists, reject accounts that are explicitly not suppliers.
      // Legacy supplier accounts without a role claim remain compatible with the
      // supplier-profile fallback already present in the Firestore rules.
      try {
        const tokenResult = await credential.user.getIdTokenResult(true);
        const role = tokenResult?.claims?.role;
        if (role && role !== "supplier") {
          await firebase.auth().signOut();
          setMessage({
            type: "error",
            text: "Access denied. This account is not registered as a Comfort Line supplier.",
          });
          return;
        }
      } catch (_) {
        // Do not block a valid legacy supplier solely because token refresh failed.
      }

      goToSupplierHome();
    } catch (err) {
      console.error("Supplier login failed:", err);
      setMessage({ type: "error", text: friendlyAuthMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const openPasswordReset = () => {
    setResetEmail(email.trim());
    setResetMessage({ type: "", text: "" });
    setResetOpen(true);
  };

  const closePasswordReset = () => {
    if (resetting) return;
    setResetOpen(false);
    setResetMessage({ type: "", text: "" });
  };

  const handlePasswordReset = async () => {
    const cleanEmail = resetEmail.trim();
    if (!cleanEmail) {
      setResetMessage({ type: "error", text: "Please enter your registered supplier email address." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setResetMessage({ type: "error", text: "Please enter a valid email address." });
      return;
    }

    try {
      setResetting(true);
      setResetMessage({ type: "", text: "" });
      await firebase.auth().sendPasswordResetEmail(cleanEmail);
      setResetMessage({
        type: "success",
        text: "If this email is registered for the Supplier Portal, a password reset link has been sent. Please check your inbox and spam folder.",
      });
    } catch (err) {
      const code = String(err?.code || "");
      if (code.includes("too-many-requests")) {
        setResetMessage({ type: "error", text: "Too many reset attempts. Please wait and try again." });
      } else if (code.includes("network-request-failed")) {
        setResetMessage({ type: "error", text: "Network error. Please check your connection and try again." });
      } else {
        setResetMessage({ type: "error", text: "Unable to send the reset email right now. Please try again." });
      }
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #0f0f0f 0%, #2a2a2a 55%, #5a0a0a 100%)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
        }}
      >
        <Paper
          elevation={12}
          sx={{
            p: { xs: 3, sm: 4 },
            maxWidth: 430,
            width: "100%",
            borderRadius: 3,
          }}
        >
          <Box textAlign="center" sx={{ mb: 2 }}>
            <Box
              component="img"
              src={logo}
              alt="Comfort Line Luxury"
              sx={{ width: 110, mb: 1.75 }}
            />
            <Typography variant="h5" fontWeight={800}>
              Supplier Portal
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage bookings, chauffeurs and trip operations securely.
            </Typography>
          </Box>

          <Stack component="form" spacing={2} onSubmit={handleLogin} noValidate>
            {message.text && <Alert severity={message.type || "info"}>{message.text}</Alert>}

            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              inputProps={{ autoCapitalize: "none", spellCheck: "false" }}
            />

            <TextField
              fullWidth
              name="password"
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />

            <Box textAlign="right">
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={openPasswordReset}
                sx={{ fontSize: "0.9rem", color: "#d32f2f" }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                bgcolor: "#d32f2f",
                color: "#fff",
                "&:hover": { bgcolor: "#b71c1c" },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: "inherit" }} /> : "Login"}
            </Button>
          </Stack>

          <Typography
            variant="caption"
            sx={{ mt: 3, color: "text.secondary", display: "block", textAlign: "center" }}
          >
            © Comfort Line Luxury Motor Vehicles Services LLC
          </Typography>
        </Paper>
      </Box>

      <Dialog open={resetOpen} onClose={closePasswordReset} fullWidth maxWidth="xs">
        <DialogTitle>Reset your password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the email address registered for your Comfort Line Supplier Portal account. We will send a secure reset link to that address.
          </DialogContentText>

          {resetMessage.text && (
            <Alert severity={resetMessage.type || "info"} sx={{ mb: 2 }}>
              {resetMessage.text}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Registered email address"
            type="email"
            value={resetEmail}
            onChange={(event) => {
              setResetEmail(event.target.value);
              if (resetMessage.text) setResetMessage({ type: "", text: "" });
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handlePasswordReset();
              }
            }}
            autoComplete="email"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closePasswordReset} disabled={resetting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePasswordReset}
            disabled={resetting}
            sx={{
              bgcolor: "#d32f2f",
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            {resetting ? <CircularProgress size={22} sx={{ color: "inherit" }} /> : "Send Reset Link"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
